import { useMemo } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { type ColumnDef } from "@tanstack/react-table"
import { FolderOpen } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { EntityTablePage } from "@/features/shared/components/entity-table-page"
import { useGroups } from "@/lib/hooks/use-groups"
import { useSites } from "@/lib/hooks/use-sites"
import type { Group } from "@/types/api"

export const Route = createFileRoute("/_authenticated/groups")({
  component: GroupsPage,
})

function getColumns(siteNameMap: Map<string, string>): ColumnDef<Group, unknown>[] {
  return [
    {
      accessorKey: "site",
      header: "Site",
      cell: ({ row }) => siteNameMap.get(row.original.site) ?? row.original.site,
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <span className="font-medium text-[var(--color-text-primary)]">
          {row.original.name}
        </span>
      ),
    },
    {
      accessorKey: "is_array",
      header: "Type",
      cell: ({ row }) => (row.original.is_array ? "Array" : "Group"),
    },
    {
      id: "measurements",
      header: "Measurements",
      cell: ({ row }) => (
        <Badge variant="secondary">
          {row.original.measurements.length}
        </Badge>
      ),
    },
  ]
}

function GroupsPage() {
  const { data: groups, isLoading, isError, error } = useGroups()
  const { data: sites } = useSites()

  const siteNameMap = useMemo(() => {
    const map = new Map<string, string>()
    sites?.forEach((s) => map.set(s.slug, s.name))
    return map
  }, [sites])

  const columns = useMemo(() => getColumns(siteNameMap), [siteNameMap])

  return (
    <EntityTablePage
      icon={<FolderOpen className="h-5 w-5 text-[var(--color-accent-green)]" />}
      title="Groups"
      description={`${groups?.length ?? 0} groups`}
      searchPlaceholder="Search groups..."
      emptyMessage="No groups"
      columns={columns}
      data={groups}
      isLoading={isLoading}
      isError={isError}
      error={error}
    />
  )
}
